import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface StudyInfo {
  studyUID: string;
  patientName: string;
  studyDate: string;
  studyDescription: string;
  modalities: string[];
  imageCount: number;
}

interface SelectableStudy extends StudyInfo {
  selected: boolean;
}

@Component({
  selector: 'app-medical-disc-picker-dialog',
  template: `
    <h2 mat-dialog-title>Import Studies from Clio Folder</h2>

    <mat-dialog-content style="min-width:500px;max-height:480px">

      <div *ngIf="loading" style="display:flex;align-items:center;gap:16px;padding:24px 0">
        <mat-spinner [diameter]="32"></mat-spinner>
        <span>Scanning Clio folder for DICOMDIR…</span>
      </div>

      <div *ngIf="error" style="color:#f44336;padding:16px 0">
        <strong>Error:</strong> {{ error }}
      </div>

      <ng-container *ngIf="!loading && !error">
        <p *ngIf="studies.length === 0" style="padding:16px 0;color:#666">
          No studies found in the DICOMDIR.
        </p>

        <div *ngFor="let s of studies"
             style="display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #eee">
          <mat-checkbox [(ngModel)]="s.selected" style="margin-top:2px"></mat-checkbox>
          <div style="flex:1">
            <div style="font-weight:500;font-size:14px">
              {{ s.patientName || 'Unknown Patient' }}
            </div>
            <div style="color:#555;font-size:12px;margin-top:2px">
              {{ formatDate(s.studyDate) }}
              <span *ngIf="s.studyDescription"> · {{ s.studyDescription }}</span>
              <span *ngIf="s.modalities.length"> · {{ s.modalities.join(', ') }}</span>
              · {{ s.imageCount }} image{{ s.imageCount === 1 ? '' : 's' }}
            </div>
          </div>
        </div>
      </ng-container>

    </mat-dialog-content>

    <mat-dialog-actions align="end" style="gap:8px">
      <button mat-button [mat-dialog-close]="null">Cancel</button>
      <button mat-raised-button color="primary"
              [disabled]="loading || !!error || !hasSelected"
              (click)="confirm()">
        Import Selected ({{ selectedCount }})
      </button>
    </mat-dialog-actions>
  `,
})
export class MedicalDiscPickerDialogComponent implements OnInit {
  loading = true;
  error: string | null = null;
  studies: SelectableStudy[] = [];

  get hasSelected(): boolean {
    return this.studies.some(s => s.selected);
  }

  get selectedCount(): number {
    return this.studies.filter(s => s.selected).length;
  }

  constructor(
    private dialogRef: MatDialogRef<MedicalDiscPickerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: { scanResult$: Promise<{ studies: StudyInfo[] }> },
  ) {}

  ngOnInit(): void {
    this.data.scanResult$
      .then(result => {
        this.studies = (result.studies || []).map(s => ({ ...s, selected: true }));
        this.loading = false;
      })
      .catch(err => {
        this.error = err?.message || 'Failed to scan Clio folder';
        this.loading = false;
      });
  }

  formatDate(d: string): string {
    if (!d || d.length < 8) return d || '—';
    return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
  }

  confirm(): void {
    const selected = this.studies.filter(s => s.selected).map(s => s.studyUID);
    this.dialogRef.close(selected);
  }
}
